// The Valistix Calendly account (ai-agency/sales/calendly/CALENDLY.md). Every booking link the dialer builds (setter
// links, follow-up emails, the campaign admin's "check the event exists" link) starts from CALENDLY_ACCOUNT_URL, so a
// handle change is this one line plus a deploy. Calendly does not redirect an old handle: change it only when the new
// one is live.
export const CALENDLY_HANDLE = 'valistix';
export const CALENDLY_ACCOUNT_URL = `https://calendly.com/${CALENDLY_HANDLE}`;

// ---------- the three setter booking links (sales/calendly/APPROVAL-three-links-2026-10-10.md, PJ's decisions 2026-10-11)
//
// Routing rule for setters: every cannabis business books Your store's growth call; a yes to getting their calls answered
// books Your missed-call fix; everything else, or not sure, books Your growth call. The Book panel (web) and the server
// (routes/meetings.ts) use the same functions below, so what the panel offers is exactly what the server accepts.

export const MISSED_CALL_SLUG = 'your-result';
export const STORE_SLUG = 'your-store';
export const GROWTH_SLUG = 'growth-call';
/** The public website link (not a setter link; a campaign may still use it as its own). */
export const FIX_LIST_SLUG = 'fix-list';
/** The secret second-meeting event: never a first meeting from a call. */
export const PLAN_SLUG = 'plan';

export type SetterLinkSlug = typeof MISSED_CALL_SLUG | typeof STORE_SLUG | typeof GROWTH_SLUG;
/** What PJ prepares before the call: the demo receptionist (version A), the store meeting, or the growth call (no fix list: the proposal comes after the call, PJ 2026-10-19). */
export type PrepKind = 'demo' | 'store' | 'growth';

export interface SetterLink {
  slug: SetterLinkSlug;
  /** The Calendly event name, as the prospect sees it. */
  name: string;
  /** true: only a cannabis business books it; false: a cannabis business never does. */
  cannabisOnly: boolean;
  /** The event carries Calendly's "03S Text 2h" reminder: a reminder number is sent when the setter got one. */
  texts: boolean;
  prep: PrepKind;
  /** One line under the option in the Book panel: who it is for. */
  whoFor: string;
  /** The panel's say-back line; {when} is the day and time in their zone. */
  sayBack: string;
}

export const SETTER_LINKS: readonly SetterLink[] = [
  {
    slug: MISSED_CALL_SLUG, name: 'Your missed-call fix', cannabisOnly: false, texts: true, prep: 'demo',
    whoFor: 'They want every call answered: the AI receptionist, missed calls, after hours. Our specialist builds a receptionist with their business name before the call.',
    sayBack: "You're booked for {when} your time with one of our specialists on Google Meet. You'll get an email from Valistix with the link. Have your cell handy, you'll dial the receptionist from it.",
  },
  {
    slug: GROWTH_SLUG, name: 'Your growth call', cannabisOnly: false, texts: true, prep: 'growth',
    whoFor: "Anything else, or they're not sure yet: a new or better website, Google and Maps, reviews, more leads, ads or marketing, growing the business.",
    sayBack: "You're booked for {when} your time with one of our specialists on Google Meet. You'll get an email from Valistix with the link and what we'll go through.",
  },
  {
    slug: STORE_SLUG, name: "Your store's growth call", cannabisOnly: true, texts: true, prep: 'store',
    whoFor: 'Every cannabis business (dispensary, CBD or THC shop). A cannabis lead is locked to it, and no other business can book it.',
    sayBack: "You're booked for {when} your time with one of our specialists on Google Meet. You'll get an email from Valistix with the link.",
  },
];

export const SETTER_LINK_SLUGS: readonly string[] = SETTER_LINKS.map((l) => l.slug);

/** Said right after the say-back line, on every link. */
export const ADD_TO_CALENDAR_LINE = "It's on its way now from Valistix. Can you see it? Tap Add to calendar in it so it's in your day.";

/** The setter's question for the reminder text (PJ 2026-10-11): a cell given for this is the consent. */
export const REMINDER_TEXT_QUESTION = "What's the best cell for a reminder text before the call?";

const clean = (slug: unknown): string => (typeof slug === 'string' ? slug.trim().replace(/^\/+|\/+$/g, '') : '');

export function setterLink(slug: string | null | undefined): SetterLink | null {
  const s = clean(slug);
  return SETTER_LINKS.find((l) => l.slug === s) ?? null;
}

/** The event's name for people: a setter link's Calendly name, the public and second-meeting events, else "/slug". */
export function bookingLinkName(slug: string | null | undefined): string {
  const s = clean(slug);
  const link = setterLink(s);
  if (link) return link.name;
  if (s === FIX_LIST_SLUG) return 'Your call with Valistix';
  if (s === PLAN_SLUG) return 'Your plan, walked through';
  return s ? `/${s}` : 'the campaign link';
}

/**
 * Events with a Calendly text workflow: the three setter links ("03S Text 2h", 2 hours before) and the public /fix-list
 * ("03 Text 24h"). Only on these does a reminder number do anything; any other or unknown event: no.
 */
export function linkSendsTexts(slug: string | null | undefined): boolean {
  const s = clean(slug);
  return (setterLink(s)?.texts ?? false) || s === FIX_LIST_SLUG;
}

/**
 * The link a lead books when nobody picked one (the Book panel's first choice, the dialer's email and text booking
 * links, an old client that sends no link): a cannabis business is locked to /your-store; anyone else gets their
 * campaign's link unless that is /your-store, and with no campaign (or a store campaign) Your growth call.
 */
export function defaultBookingSlug(campaignSlug: string | null | undefined, cannabis: boolean): string {
  if (cannabis) return STORE_SLUG;
  const own = clean(campaignSlug);
  return own && own !== STORE_SLUG ? own : GROWTH_SLUG;
}

/**
 * The link in a booking link the dialer sends for them to book themselves (Send email, the AI text draft, "Insert
 * booking link"): defaultBookingSlug when we know whether the business is cannabis; when the caller can't tell
 * (`cannabis` undefined, e.g. the Texts page), the campaign's own link as it is, and Your growth call with no campaign.
 */
export function selfBookingSlug(campaignSlug: string | null | undefined, cannabis?: boolean): string {
  if (cannabis === undefined) return clean(campaignSlug) || GROWTH_SLUG;
  return defaultBookingSlug(campaignSlug, cannabis);
}

export interface BookingOptions {
  /** The links this lead may book, the default first. */
  slugs: string[];
  defaultSlug: string;
  /** A cannabis lead: /your-store only, no choice shown. */
  locked: boolean;
}

/**
 * What the Book panel offers ("Book into") and the server accepts. A cannabis business or campaign: /your-store only.
 * Anyone else: their campaign's own link (unless it is /your-store) and the non-cannabis setter links, never
 * /your-store. /plan, /fix-list and any other link only when it is the campaign's own.
 */
export function bookingOptions(campaignSlug: string | null | undefined, cannabis: boolean): BookingOptions {
  if (cannabis) return { slugs: [STORE_SLUG], defaultSlug: STORE_SLUG, locked: true };
  const defaultSlug = defaultBookingSlug(campaignSlug, false);
  const slugs = [defaultSlug];
  for (const l of SETTER_LINKS) if (!l.cannabisOnly && !slugs.includes(l.slug)) slugs.push(l.slug);
  return { slugs, defaultSlug, locked: false };
}

/**
 * The strict cannabis lock on a campaign's own link (APPROVAL-three-links 6.3): a cannabis campaign is set to
 * /your-store only, and no other campaign is ever set to it. null when the pair is fine. The admin page
 * (web/src/admin/validate.ts calendlySlugProblems) and the server (routes/campaigns.ts create and PATCH) share it.
 */
export function campaignLinkProblem(slug: string | null | undefined, cannabis: boolean): string | null {
  const s = clean(slug);
  if (cannabis && s !== STORE_SLUG) return `A cannabis campaign books on ${STORE_SLUG} (Your store's growth call) only.`;
  if (!cannabis && s === STORE_SLUG) return `${STORE_SLUG} is for cannabis campaigns only. Use ${MISSED_CALL_SLUG} (a yes to getting calls answered) or ${GROWTH_SLUG} (everything else).`;
  return null;
}

export type SlugResolution = { ok: true; slug: string } | { ok: false; message: string };

/** The one rule for a requested link (the server refuses anything else before calling Calendly). No link = the default. */
export function resolveBookingSlug(requested: unknown, campaignSlug: string | null | undefined, cannabis: boolean): SlugResolution {
  const opts = bookingOptions(campaignSlug, cannabis);
  if (requested === undefined || requested === null || requested === '') return { ok: true, slug: opts.defaultSlug };
  if (typeof requested !== 'string') return { ok: false, message: 'Pick one of the booking links.' };
  const s = clean(requested);
  if (opts.slugs.includes(s)) return { ok: true, slug: s };
  if (cannabis) return { ok: false, message: "Cannabis: books on Your store's growth call." };
  if (s === STORE_SLUG) return { ok: false, message: "Your store's growth call is for cannabis businesses only. Book this one on Your missed-call fix or Your growth call." };
  return { ok: false, message: `${bookingLinkName(s)} isn't a booking link for this lead. Book it on ${opts.slugs.map(bookingLinkName).join(' or ')}.` };
}
